import { createContext, useContext, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';
import type { GridApi } from 'ag-grid-community';

import type { BankFeedAccountType, BankFeedSummary, BankFeedTransactionRow } from '@/domain/bankFeed/schemas';
import type { ToolbarFilters } from '@/pages/company/BankFeedRoute/components/FilterToolbar/types';

// Shared imperative handle on the AG Grid instance. Parent route owns the
// ref, BankFeedGrid populates it on `onGridReady` and clears it on unmount;
// consumers (ExportButton) read grid state directly at click time. Chosen
// over a callback-pushed snapshot state because (a) AG Grid already is the
// source of truth — duplicating into React state caused re-renders on every
// sort/filter/column-visibility change, and (b) ref-read is always fresh.
type BankFeedGridApiRef = MutableRefObject<GridApi<BankFeedTransactionRow> | null>;

export type BankFeedRouteContext = {
  // The parent route owns the ONLY `useBankFeedSummary` observer and shares the
  // result here; every in-route consumer (FilterToolbar, ExportButton, the Outlet
  // child grids) reads summary from this context instead of opening its own
  // `useBankFeedTransactionsSummary`. A second observer would mount after the
  // currency gate opens (post first fetch) and, under `staleTime: 0`, fire a
  // redundant summary refetch — see @/domain/bankFeed/queries/transactions.ts.
  summary: BankFeedSummary | null;
  // Loading flag from that same single observer, for consumers gating UI on the
  // initial summary fetch (FilterToolbar's Currency Select).
  isSummaryLoading: boolean;
  toolbarFilters: ToolbarFilters;
  setToolbarFilters: Dispatch<SetStateAction<ToolbarFilters>>;
  accountType: BankFeedAccountType;
  gridApiRef: BankFeedGridApiRef;
};

// Plain React Context (not react-router's outletContext) so consumers can sit
// either inside the Outlet tree (BankFeedGrid via BankTransactionsRoute) or
// outside it (ExportButton next to FilterToolbar). useOutletContext is scoped
// to Outlet descendants — using it would force layout decisions that don't
// serve any other purpose.
const Ctx = createContext<BankFeedRouteContext | null>(null);

export const BankFeedRouteProvider = Ctx.Provider;

export const useBankFeedRouteContext = (): BankFeedRouteContext => {
  const value = useContext(Ctx);
  if (value === null) {
    throw new Error('useBankFeedRouteContext must be used within <BankFeedRouteProvider>');
  }
  return value;
};
